
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const useGranularFieldUpdater = () => {
  const queryClient = useQueryClient();

  const updateExerciseField = useCallback((
    routineId: string,
    exerciseId: string,
    fieldName: string,
    newValue: any
  ) => {
    // Update useExercises query
    queryClient.setQueryData(["exercises", routineId], (oldData: any[]) => {
      if (!oldData) return oldData;
      
      return oldData.map(exercise => {
        if (exercise.id === exerciseId) {
          console.log(`🎯 Granular field update: ${exerciseId}.${fieldName} =`, newValue);
          return { ...exercise, [fieldName]: newValue };
        }
        return exercise;
      });
    });

    // Update useWorkoutPlanRoutines query for all plans that contain this routine
    queryClient.setQueriesData(
      { queryKey: ["workout-plan-routines"] },
      (oldData: any[]) => {
        if (!oldData) return oldData;
        
        return oldData.map(routine => {
          if (routine.id === routineId) {
            const updatedExercises = routine.exercises?.map((exercise: any) => {
              if (exercise.id === exerciseId) {
                return { ...exercise, [fieldName]: newValue };
              }
              return exercise;
            }) || [];
            
            return { ...routine, exercises: updatedExercises };
          }
          return routine;
        });
      }
    );

    // Update useOptimizedWorkoutPlan query for all plans
    queryClient.setQueriesData(
      { queryKey: ["workout-plan-optimized"] },
      (oldData: any) => {
        if (!oldData?.routines) return oldData;
        
        const updatedRoutines = oldData.routines.map((routine: any) => {
          if (routine.id === routineId) {
            const updatedExercises = routine.exercises?.map((exercise: any) => {
              if (exercise.id === exerciseId) {
                return { ...exercise, [fieldName]: newValue };
              }
              return exercise;
            }) || [];
            
            return { ...routine, exercises: updatedExercises };
          }
          return routine;
        });
        
        return { ...oldData, routines: updatedRoutines };
      }
    );

    console.log(`✅ Granular update completed for field ${fieldName} in exercise ${exerciseId}`);
  }, [queryClient]);

  const updateRoutineField = useCallback((
    routineId: string,
    fieldName: string,
    newValue: any
  ) => {
    // Update useWorkoutPlanRoutines query
    queryClient.setQueriesData(
      { queryKey: ["workout-plan-routines"] },
      (oldData: any[]) => {
        if (!oldData) return oldData;
        
        return oldData.map(routine => {
          if (routine.id === routineId) {
            console.log(`🎯 Granular routine update: ${routineId}.${fieldName} =`, newValue);
            return { ...routine, [fieldName]: newValue };
          }
          return routine;
        });
      }
    );

    // Update useOptimizedWorkoutPlan query
    queryClient.setQueriesData(
      { queryKey: ["workout-plan-optimized"] },
      (oldData: any) => {
        if (!oldData?.routines) return oldData;
        
        const updatedRoutines = oldData.routines.map((routine: any) => {
          if (routine.id === routineId) {
            return { ...routine, [fieldName]: newValue };
          }
          return routine;
        });
        
        return { ...oldData, routines: updatedRoutines };
      }
    );

    console.log(`✅ Granular routine update completed for field ${fieldName} in routine ${routineId}`);
  }, [queryClient]);

  return {
    updateExerciseField,
    updateRoutineField
  };
};
